/* eslint-disable no-underscore-dangle */
/* eslint-disable no-use-before-define */
import dayjs from 'dayjs'
import {
	Box,
	IconButton,
	Text,
	useColorModeValue,
	useMediaQuery,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, FormProvider } from 'react-hook-form'
import { AddIcon } from '@chakra-ui/icons'
import { useParams } from 'react-router-dom'
import React from 'react'

import { useModalState } from 'app/utils/useModalState'
import { scheduleFormSchema } from 'modules/schedule/validations/scheduleFormSchema'
import { useUpdateSchedule } from 'modules/schedule/queries/useUpdateSchedule'
import { FileIcon } from 'app/icons/FileIcon'
import { Settings } from 'modules/schedule/validations/settingsSchema'
import { useCreateSchedule } from 'modules/schedule/queries/useCreateSchedule'

import type { Schedule } from 'modules/schedule/queries/useProfessionalSchedule'
import { ScheduleTableRoot } from './ScheduleTableRoot'
import EditScheduleModal from '../EditScheduleModal'
import ScheduleModal from '../ScheduleModal'
import { useAbility } from 'ability'

function ScheduleFormProvider({
	children,
	defaultValues,
}: {
	children: React.ReactNode
	defaultValues: Record<string, unknown>
}) {
	const form = useForm({
		defaultValues,
		resolver: yupResolver(scheduleFormSchema),
	})

	return <FormProvider {...form}>{children}</FormProvider>
}

interface Props {
	events: Schedule['events']
	hasAnnotation?: boolean
	onAnnotationIconPress?: () => void
	disabled?: boolean
	settings: Settings
	allEvents: Schedule['events']
	start?: string
	end?: string

	useCellWidth?: boolean
	columnIndex?: number
}

const ENABLE_ADD_BUTTON = false

export function ScheduleCell({
	events,
	hasAnnotation,
	disabled,
	onAnnotationIconPress,
	settings,
	allEvents,
	start,
	end,
	useCellWidth,
	columnIndex,
}: Props) {
	const createScheduleModal = useModalState()

	const createSchedule = useCreateSchedule()

	const getCellBg = () => {
		if (disabled) return useColorModeValue('#f0f0f0', '#2a3f54')
		return useColorModeValue('#fff', 'transparent')
	}

	const canCreateSchedule = useAbility().can('create', 'Appointment')

	return (
		<ScheduleTableRoot.Cell
			bg={getCellBg()}
			fontWeight={600}
			fontSize={{ sm: 'xs', md: 'sm' }}
			width={0}
			height="48px"
			py="9px"
			px="10px"
			onClick={() => {
				if (events.length < 2 && !disabled && canCreateSchedule) {
					createScheduleModal.open()
				}
			}}
		>
			<Box
				as="ul"
				display="grid"
				gridAutoFlow="column"
				alignItems="center"
				gap={3}
				listStyleType="none"
			>
				{ENABLE_ADD_BUTTON && !disabled && (
					<IconButton
						rounded="full"
						bg="white"
						size="xs"
						icon={<AddIcon />}
						aria-label="Novo agendamento"
					/>
				)}

				{events.map((event: any) => {
					return (
						<ScheduleEvent
							key={event.id}
							event={event}
							settings={settings}
							allEvents={allEvents}
							useCellWidth={useCellWidth}
							columnIndex={columnIndex}
						/>
					)
				})}

				{hasAnnotation && (
					<Box
						ml="auto"
						color="red.400"
						onClick={onAnnotationIconPress}
					>
						<FileIcon size={24} />
					</Box>
				)}

				<ScheduleFormProvider
					key={start + createScheduleModal.isOpen} // recreates form on date change
					defaultValues={{
						start,
						end,
					}}
				>
					<ScheduleModal
						isOpen={createScheduleModal.isOpen}
						onClose={createScheduleModal.close}
						onSubmit={(values) =>
							createSchedule.mutate(values, {
								onSuccess: createScheduleModal.close,
							})
						}
					/>
				</ScheduleFormProvider>
			</Box>
		</ScheduleTableRoot.Cell>
	)
}

interface ScheduleEventProps {
	event: Schedule['events'][number]
	settings: Settings
	allEvents: Schedule['events']
	useCellWidth?: boolean
	columnIndex?: number
}

function ScheduleEvent({
	event,
	settings,
	allEvents,
	useCellWidth,
	columnIndex,
}: ScheduleEventProps) {
	const editScheduleModal = useModalState()
	const updateSchedule = useUpdateSchedule()

	const canUpdateSchedule = useAbility().can('update', 'Appointment')
	const statusBgMap: Record<string, string> = {
		waiting: useColorModeValue('yellow.100', 'yellow.400'),
		cancelled: useColorModeValue('red.100', 'red.400'),
		finished: useColorModeValue('green.100', 'green.400'),
		scheduled: useColorModeValue('blue.100', 'blue.400'),
		confirmed: useColorModeValue('blue.400', 'blue.700'),
	}
	const getBg = () => {
		return statusBgMap[event.status] ?? undefined
	}

	const eventEndDayjs = dayjs(event.end)
	const eventStartDayjs = dayjs(event.start)
	const sessionDurationHours = settings.agenda.sessionDurationMinutes / 60

	const [agendaStartHourString, agendaStartMinuteString] =
		settings.agenda.startTime.split(':')
	const agendaStartDayjs = dayjs(event.start)
		.hour(Number(agendaStartHourString))
		.minute(Number(agendaStartMinuteString))

	const matchesWithOverlap = allEvents.filter((item) => {
		if (item.id === event.id) {
			return false
		}
		const hasSameOrBeforeStart = dayjs(eventStartDayjs).isSameOrBefore(
			item.start
		)
		const hasSameOrAfterEnd =
			dayjs(item.end).isSame(eventEndDayjs) ||
			dayjs(eventEndDayjs).isAfter(item.end)
		return hasSameOrBeforeStart && hasSameOrAfterEnd
	})

	function getTop() {
		const layoutTop = 0

		// formula (hora_inicial_do_evento - hora_inicial_da_agenda ) * (tempo_de_sessao_em_hora) ** -1
		const eventStartHours =
			eventStartDayjs.hour() + eventStartDayjs.minute() / 60
		const agendaStartHours =
			agendaStartDayjs.hour() + agendaStartDayjs.minute() / 60

		const v =
			(eventStartHours - agendaStartHours) * sessionDurationHours ** -1
		const value = v * 48 /* cell height */ + 10 /* vertical padding */
		return `${layoutTop + value}px`
	}
	function getHeight() {
		const diff = eventEndDayjs.diff(eventStartDayjs)
		const hoursDiff = diff / 1000 / 60 / 60
		const v = hoursDiff - sessionDurationHours
		if (v > 0 && v < 1) {
			const value =
				v ** -1 * 28 /* cell height */ +
				v ** -1 * 10 /* vertical padding */
			return `${value}px`
		}
		if (v === 1) {
			const value =
				hoursDiff * (1 / sessionDurationHours) * 28 /* cell height */ +
				hoursDiff *
					(1 / sessionDurationHours) *
					10 /* vertical padding */
			return `${value}px`
		}
		if (v >= 1) {
			const k = hoursDiff * (1 / sessionDurationHours)
			const value =
				k * 28 /* cell height */ +
				10 /* start padding */ +
				(k - 2) * 20 /* vertical middle padding */ +
				10 /* end padding */
			// (k - 2) * 1 /* border between cells */
			return `${value}px`
		}

		return '28px'
	}
	function getLeft() {
		const overlappingEvents = getOverlappingEvents(event, allEvents)

		const canvas = document.createElement('canvas')
		const context = canvas.getContext('2d')

		// Lista de eventos incluindo o atual
		const sortedEvents = [...overlappingEvents, event].sort((a, b) => {
			const diff = dayjs(a.start).diff(dayjs(b.start))
			return diff !== 0 ? diff : a.id.localeCompare(b.id)
		})

		// Qual é o índice do evento atual dentro da lista?
		const index = sortedEvents.findIndex((e) => e.id === event.id)

		// Calcula o offset somando o "width estimado" dos eventos anteriores
		let leftOffset = 54 + 10 // time column + initial padding

		for (let i = 0; i < index; i++) {
			const name = sortedEvents[i].customerInfo?.name ?? ''
			const width = context.measureText(name).width + 60 // label width + base padding
			leftOffset += width
		}

		return `${leftOffset}px`
	}

	const { type } = useParams()
	const isWeekTable = type === 'week'

	const scheduleBorder = useColorModeValue('#e0e0e0', 'transparent')

	const [isLargeScreen] = useMediaQuery('(min-width: 48em)')

	return (
		<Text
			ref={(instance: HTMLElement) => {
				if (instance && useCellWidth) {
					const parent = instance.parentElement.parentElement

					const relativeEvents = allEvents.filter((it) =>
						dayjs(it.start).isSame(eventStartDayjs, 'date')
					)
					const maxOverlaps =
						getMaxSimultaneousOverlaps(event, relativeEvents) + 1
					const overlapIndex = getOverlapIndex(event, relativeEvents)

					const padding = isLargeScreen ? 56 : 40
					const width = parent.clientWidth / maxOverlaps

					instance.style.width = `${width}px`
					instance.style.left = `${
						width * overlapIndex +
						padding +
						(columnIndex ?? 0) * parent.clientWidth
					}px`
					instance.style.paddingRight = '4px'
					instance.style.paddingLeft = '4px'
				}
			}}
			flex={1}
			px="10px"
			py="4px"
			borderRadius={3}
			border={`1px solid ${scheduleBorder}`}
			bg={getBg()}
			as="li"
			width="min-content"
			onClick={(e) => {
				e.stopPropagation()
				if (canUpdateSchedule) {
					editScheduleModal.open()
				}
			}}
			textOverflow="ellipsis"
			whiteSpace="nowrap"
			overflow="hidden"
			position={'absolute'}
			height={getHeight()}
			top={getTop()}
			left={isWeekTable ? undefined : getLeft()}
			right={isWeekTable ? undefined : 0}
			maxWidth={isWeekTable ? undefined : 'fit-content'}
		>
			{event?.customerInfo.name}
			<ScheduleFormProvider
				defaultValues={{
					start: event?.start
						? dayjs(event.start).format('YYYY-MM-DDTHH:mm:ss')
						: undefined,
					end: event?.end
						? dayjs(event.end).format('YYYY-MM-DDTHH:mm:ss')
						: undefined,
					customerId: event?.customerInfo._id,
					id: event?.id,
					cellphoneNumber: event?.customerInfo.cellphoneNumber,
					status: event?.status,
					notes: event?.notes,
					title: event?.customerInfo.cellphoneNumber,
				}}
			>
				<EditScheduleModal
					isOpen={editScheduleModal.isOpen}
					onClose={editScheduleModal.close}
					onSubmit={(values) => {
						updateSchedule.mutate(values, {
							onSuccess() {
								editScheduleModal.close()
							},
						})
					}}
					event={event}
				/>
			</ScheduleFormProvider>
		</Text>
	)
}

function getMaxSimultaneousOverlaps(
	event: Schedule['events'][number],
	allEvents: Schedule['events']
) {
	const eventStart = dayjs(event.start)
	const eventEnd = dayjs(event.end)

	const overlappingEvents = allEvents.filter((item) => {
		if (item.id === event.id) return false

		const itemStart = dayjs(item.start)
		const itemEnd = dayjs(item.end)

		return eventStart.isBefore(itemEnd) && itemStart.isBefore(eventEnd)
	})

	// Dividir o tempo do evento em blocos (ex: a cada 5 minutos)
	const intervalMinutes = 5
	let maxOverlaps = 0

	for (
		let minute = 0;
		minute < eventEnd.diff(eventStart, 'minute');
		minute += intervalMinutes
	) {
		const currentTime = eventStart.add(minute, 'minute')

		let overlaps = 0
		for (const other of overlappingEvents) {
			const otherStart = dayjs(other.start)
			const otherEnd = dayjs(other.end)

			if (
				(currentTime.isSame(otherStart) ||
					currentTime.isAfter(otherStart)) &&
				currentTime.isBefore(otherEnd)
			) {
				overlaps++
			}
		}

		maxOverlaps = Math.max(maxOverlaps, overlaps)
	}

	return maxOverlaps
}

function getOverlapIndex(
	event: Schedule['events'][number],
	allEvents: Schedule['events']
) {
	const eventStart = dayjs(event.start)
	const eventEnd = dayjs(event.end)

	const overlapping = allEvents
		.filter((item) => {
			if (item.id === event.id) return false
			const start = dayjs(item.start)
			const end = dayjs(item.end)
			return eventStart.isBefore(end) && start.isBefore(eventEnd)
		})
		.sort((a, b) => dayjs(a.start).diff(b.start)) // Ordenar por início

	let index = 0
	for (const item of overlapping) {
		const itemStart = dayjs(item.start)
		const itemEnd = dayjs(item.end)

		if (
			itemStart.isBefore(eventStart) ||
			(itemStart.isSame(eventStart) && item.id < event.id) // desempate por id
		) {
			index++
		}
	}

	return index
}
function getOverlappingEvents(
	event: Schedule['events'][number],
	allEvents: Schedule['events']
) {
	return allEvents
		.filter((e) => {
			if (e.id === event.id) return false
			return (
				dayjs(event.start).isBefore(e.end) &&
				dayjs(e.start).isBefore(event.end)
			)
		})
		.sort((a, b) => {
			// Ordenação estável
			const diff = dayjs(a.start).diff(dayjs(b.start))
			return diff !== 0 ? diff : a.id.localeCompare(b.id)
		})
}
